import React, { useState } from 'react';
import { Booking, BookingStatus, PaymentStatus, AdminUpdateBookingDto } from '../../types/booking';
import { bookingService } from '../../services/bookingService';

interface BookingDetailsEditableViewProps {
  booking: Booking;
  onClose: () => void;
  onUpdate: (booking: Booking) => void;
}

const BookingDetailsEditableView: React.FC<BookingDetailsEditableViewProps> = ({ 
  booking, 
  onClose,
  onUpdate 
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Location options matching the website
  const locationOptions = [
    { value: 'East Perth', label: 'East Perth' },
    { value: 'Crown Plaza', label: 'Crown Plaza' },
    { value: 'Perth Airport', label: 'Perth Airport (+$50)' },
  ];

  // Available extras options
  const availableExtras = [
    { name: 'Child Seat', price: 15, description: 'Per day' },
    { name: 'GPS Navigation', price: 10, description: 'Per rental' },
    { name: 'Additional Driver', price: 20, description: 'Per rental' },
    { name: 'Damage Waiver', price: 25, description: 'Per day' },
    { name: 'Roadside Assistance', price: 15, description: 'Per rental' },
  ];

  // Helper function to format address (can be string or object)
  const formatAddress = (address: any): string => {
    if (!address) return '';
    if (typeof address === 'string') return address;
    if (typeof address === 'object') {
      const parts = [
        address.street,
        address.city,
        address.state,
        address.zipCode,
        address.country
      ].filter(Boolean);
      return parts.join(', ');
    }
    return '';
  };

  const [formData, setFormData] = useState({
    status: booking.status,
    paymentStatus: booking.paymentStatus,
    pickUpDate: new Date(booking.pickUpDate).toISOString().split('T')[0],
    pickUpTime: booking.pickUpTime,
    pickUpLocation: booking.pickUpLocation,
    returnDate: new Date(booking.returnDate).toISOString().split('T')[0],
    returnTime: booking.returnTime,
    returnLocation: booking.returnLocation,
    totalAmount: booking.totalAmount,
    paidAmount: booking.paidAmount,
    lateFees: booking.lateFees || 0,
    damageFees: booking.damageFees || 0,
    cleaningFees: booking.cleaningFees || 0,
    additionalCharges: booking.additionalCharges || 0,
    taxAmount: booking.taxAmount || 0,
    insuranceAmount: booking.insuranceAmount || 0,
    discountAmount: booking.discountAmount || 0,
    startMileage: booking.startMileage,
    endMileage: booking.endMileage,
    fuelLevelStart: booking.fuelLevelStart,
    fuelLevelEnd: booking.fuelLevelEnd,
    specialRequests: booking.specialRequests,
    internalNotes: booking.internalNotes,
    customerNotes: booking.customerNotes,
    // Customer fields
    customerFirstName: booking.customer?.firstName || '',
    customerLastName: booking.customer?.lastName || '',
    customerEmail: booking.customer?.email || '',
    customerPhone: booking.customer?.contact || booking.customer?.phone || '',
    customerLicenseNumber: booking.customer?.licenseNumber || '',
    customerAddress: formatAddress(booking.customer?.address),
    // Extras
    extras: (booking.extras || []).map((e: any) => ({
      name: e.name || e.title || '',
      price: e.price || e.amount || 0,
      quantity: e.quantity || 1,
      description: e.description || ''
    })),
  });

  // Calculate airport charges - $50 per airport location (pickup and/or dropoff)
  const calculateAirportCharges = (pickupLoc: string, returnLoc: string) => {
    let charges = 0;
    if (pickupLoc?.toLowerCase().includes('air')) {
      charges += 50;
    }
    if (returnLoc?.toLowerCase().includes('air')) {
      charges += 50;
    }
    return charges;
  };

  const airportCharges = calculateAirportCharges(
    isEditMode ? formData.pickUpLocation : booking.pickUpLocation,
    isEditMode ? formData.returnLocation : booking.returnLocation
  );

  // Extras management functions
  const handleAddExtra = (extraName: string) => {
    const extraTemplate = availableExtras.find(e => e.name === extraName);
    if (extraTemplate) {
      setFormData(prev => ({
        ...prev,
        extras: [...prev.extras, { ...extraTemplate, quantity: 1 }]
      }));
    }
  };

  const handleRemoveExtra = (index: number) => {
    setFormData(prev => ({
      ...prev,
      extras: prev.extras.filter((_: any, i: number) => i !== index)
    }));
  };

  const handleExtraChange = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      extras: prev.extras.map((extra: any, i: number) => 
        i === index ? { ...extra, [field]: field === 'price' || field === 'quantity' ? parseFloat(value) || 0 : value } : extra
      )
    }));
  };

  // Calculate extras total
  const calculateExtrasTotal = () => {
    return formData.extras.reduce((total: number, extra: any) => {
      return total + (extra.price || 0) * (extra.quantity || 1);
    }, 0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Build the update data with proper structure
      const updateData: any = {
        status: formData.status,
        paymentStatus: formData.paymentStatus,
        pickUpDate: formData.pickUpDate,
        pickUpTime: formData.pickUpTime,
        pickUpLocation: formData.pickUpLocation,
        returnDate: formData.returnDate,
        returnTime: formData.returnTime,
        returnLocation: formData.returnLocation,
        totalAmount: formData.totalAmount,
        paidAmount: formData.paidAmount,
        lateFees: formData.lateFees,
        damageFees: formData.damageFees,
        cleaningFees: formData.cleaningFees,
        additionalCharges: formData.additionalCharges,
        taxAmount: formData.taxAmount,
        insuranceAmount: formData.insuranceAmount,
        discountAmount: formData.discountAmount,
        startMileage: formData.startMileage,
        endMileage: formData.endMileage,
        fuelLevelStart: formData.fuelLevelStart,
        fuelLevelEnd: formData.fuelLevelEnd,
        specialRequests: formData.specialRequests,
        internalNotes: formData.internalNotes,
        customerNotes: formData.customerNotes,
        // Customer data
        customer: {
          firstName: formData.customerFirstName,
          lastName: formData.customerLastName,
          email: formData.customerEmail,
          phone: formData.customerPhone,
          contact: formData.customerPhone,
          licenseNumber: formData.customerLicenseNumber,
          address: formData.customerAddress,
        },
        // Extras
        extras: formData.extras,
      };

      console.log('📝 Saving booking changes from editable view:', updateData);
      const response = await bookingService.updateBooking(booking._id, updateData);
      
      console.log('📡 Update response:', response);
      
      if (response.success && response.data) {
        console.log('✅ Booking updated successfully:', response.data);
        setSuccess('Booking updated successfully!');
        onUpdate(response.data);
        setIsEditMode(false);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorMsg = response.error?.message || 'Failed to update booking';
        console.error('❌ Update failed:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('❌ Error updating booking:', err);
      setError('An error occurred while updating the booking');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original booking values
    setFormData({
      status: booking.status,
      paymentStatus: booking.paymentStatus,
      pickUpDate: new Date(booking.pickUpDate).toISOString().split('T')[0],
      pickUpTime: booking.pickUpTime,
      pickUpLocation: booking.pickUpLocation || '',
      returnDate: new Date(booking.returnDate).toISOString().split('T')[0],
      returnTime: booking.returnTime,
      returnLocation: booking.returnLocation || '',
      totalAmount: booking.totalAmount,
      paidAmount: booking.paidAmount,
      lateFees: booking.lateFees || 0,
      damageFees: booking.damageFees || 0,
      cleaningFees: booking.cleaningFees || 0,
      additionalCharges: booking.additionalCharges || 0,
      taxAmount: booking.taxAmount || 0,
      insuranceAmount: booking.insuranceAmount || 0,
      discountAmount: booking.discountAmount || 0,
      startMileage: booking.startMileage,
      endMileage: booking.endMileage,
      fuelLevelStart: booking.fuelLevelStart,
      fuelLevelEnd: booking.fuelLevelEnd,
      specialRequests: booking.specialRequests,
      internalNotes: booking.internalNotes,
      customerNotes: booking.customerNotes,
      // Customer fields
      customerFirstName: booking.customer?.firstName || '',
      customerLastName: booking.customer?.lastName || '',
      customerEmail: booking.customer?.email || '',
      customerPhone: booking.customer?.contact || booking.customer?.phone || '',
      customerLicenseNumber: booking.customer?.licenseNumber || '',
      customerAddress: formatAddress(booking.customer?.address),
      // Extras
      extras: (booking.extras || []).map((e: any) => ({
        name: e.name || e.title || '',
        price: e.price || e.amount || 0,
        quantity: e.quantity || 1,
        description: e.description || ''
      })),
    });
    setIsEditMode(false);
    setError(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (date: Date | string) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: BookingStatus) => {
    const colorClass = bookingService.getStatusColor(status);
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}>
        {bookingService.formatStatus(status)}
      </span>
    );
  };

  const getPaymentStatusBadge = (status: PaymentStatus) => {
    const colorClass = bookingService.getPaymentStatusColor(status);
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colorClass}`}>
        {bookingService.formatStatus(status)}
      </span>
    );
  };

  const calculateDuration = () => {
    if (isEditMode) {
      const pickup = new Date(formData.pickUpDate);
      const returnDate = new Date(formData.returnDate);
      const diffTime = Math.abs(returnDate.getTime() - pickup.getTime());
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
    return bookingService.calculateBookingDuration(
      new Date(booking.pickUpDate),
      new Date(booking.returnDate)
    );
  };

  const calculateTotalCharges = () => {
    const data = isEditMode ? formData : booking;
    const base = data.totalAmount || 0;
    const late = data.lateFees || 0;
    const damage = data.damageFees || 0;
    const cleaning = data.cleaningFees || 0;
    const additional = data.additionalCharges || 0;
    const tax = data.taxAmount || 0;
    const insurance = data.insuranceAmount || 0;
    const discount = data.discountAmount || 0;
    
    // Include airport charges in total
    return base + late + damage + cleaning + additional + tax + insurance + airportCharges - discount;
  };

  return (
    <div className="max-h-[80vh] overflow-y-auto">
      <div className="space-y-6 p-6">
        {/* Success/Error Messages */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-green-800 font-medium">✓ {success}</p>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-800 font-medium">✗ {error}</p>
          </div>
        )}

        {/* Header Section */}
        <div className="border-b border-gray-200 pb-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold text-gray-900">
              {booking.bookingReference}
            </h2>
            <div className="flex gap-2 items-center">
              {!isEditMode ? (
                <>
                  {getStatusBadge(booking.status)}
                  {getPaymentStatusBadge(booking.paymentStatus)}
                </>
              ) : (
                <span className="text-sm text-blue-600 font-medium">✎ Editing Mode</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>Created: {formatDateTime(booking.createdAt)}</span>
            {booking.updatedAt && (
              <span>Updated: {formatDateTime(booking.updatedAt)}</span>
            )}
            {((booking as any).source === 'manual' || (booking as any).source === 'dashboard' || (booking as any).isManual) ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Manual Booking
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd" />
                </svg>
                Website Booking
              </span>
            )}
          </div>
        </div>

        {/* Status Section - Editable */}
        {isEditMode && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Booking Status</h3>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Booking Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.values(BookingStatus).map(status => (
                      <option key={status} value={status}>
                        {bookingService.formatStatus(status)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Status
                  </label>
                  <select
                    name="paymentStatus"
                    value={formData.paymentStatus}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.values(PaymentStatus).map(status => (
                      <option key={status} value={status}>
                        {bookingService.formatStatus(status)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customer Information - Editable */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Customer Information</h3>
          <div className={`rounded-lg p-4 ${isEditMode ? 'bg-blue-50' : 'bg-gray-50'}`}>
            {isEditMode ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    name="customerFirstName"
                    value={formData.customerFirstName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    name="customerLastName"
                    value={formData.customerLastName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Last name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="customerEmail"
                    value={formData.customerEmail}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Email address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    name="customerPhone"
                    value={formData.customerPhone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
                  <input
                    type="text"
                    name="customerLicenseNumber"
                    value={formData.customerLicenseNumber}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Driver's license number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    name="customerAddress"
                    value={formData.customerAddress}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Address"
                  />
                </div>
              </div>
            ) : booking.customer ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-600">Name:</span>
                  <p className="font-medium text-gray-900">
                    {booking.customer.firstName || ''} {booking.customer.lastName || ''}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Email:</span>
                  <p className="font-medium text-gray-900">{booking.customer.email || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Phone:</span>
                  <p className="font-medium text-gray-900">
                    {booking.customer.contact || booking.customer.phone || 'N/A'}
                  </p>
                </div>
                {booking.customer.licenseNumber && (
                  <div>
                    <span className="text-sm text-gray-600">License Number:</span>
                    <p className="font-medium text-gray-900">{booking.customer.licenseNumber}</p>
                  </div>
                )}
                {booking.customer.address && (
                  <div>
                    <span className="text-sm text-gray-600">Address:</span>
                    <p className="font-medium text-gray-900">{formatAddress(booking.customer.address)}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 italic">Customer information not available</p>
            )}
          </div>
        </div>

        {/* Vehicle Information - Editable (display only, car change requires separate flow) */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Vehicle Information</h3>
          <div className={`rounded-lg p-4 ${isEditMode ? 'bg-blue-50' : 'bg-gray-50'}`}>
            {booking.car ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-600">Vehicle:</span>
                  <p className="font-medium text-gray-900">{booking.car.name || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Model:</span>
                  <p className="font-medium text-gray-900">
                    {booking.car.brand || ''} {booking.car.model || ''} {booking.car.year ? `(${booking.car.year})` : ''}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Registration:</span>
                  <p className="font-medium text-gray-900">{booking.car.registrationNumber || booking.car.licensePlate || 'N/A'}</p>
                </div>
                {booking.car.category && (
                  <div>
                    <span className="text-sm text-gray-600">Category:</span>
                    <p className="font-medium text-gray-900">{booking.car.category}</p>
                  </div>
                )}
                {booking.car.color && (
                  <div>
                    <span className="text-sm text-gray-600">Color:</span>
                    <p className="font-medium text-gray-900">{booking.car.color}</p>
                  </div>
                )}
                {isEditMode && (
                  <div className="col-span-2 mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
                    ℹ️ To change the vehicle, please create a new booking or use the Fleet Management section.
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 italic">Vehicle information not available</p>
            )}
          </div>
        </div>

        {/* Rental Period - Editable */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Rental Period</h3>
          <div className={`rounded-lg p-4 ${isEditMode ? 'bg-blue-50' : 'bg-gray-50'}`}>
            {isEditMode ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Date</label>
                    <input
                      type="date"
                      name="pickUpDate"
                      value={formData.pickUpDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Time</label>
                    <input
                      type="time"
                      name="pickUpTime"
                      value={formData.pickUpTime}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Return Date</label>
                    <input
                      type="date"
                      name="returnDate"
                      value={formData.returnDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Return Time</label>
                    <input
                      type="time"
                      name="returnTime"
                      value={formData.returnTime}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Location</label>
                    <select
                      name="pickUpLocation"
                      value={formData.pickUpLocation || ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Location</option>
                      {locationOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {formData.pickUpLocation?.toLowerCase().includes('air') && (
                      <p className="text-xs text-orange-600 mt-1">✈️ +$50 airport pickup charge</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Return Location</label>
                    <select
                      name="returnLocation"
                      value={formData.returnLocation || ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Location</option>
                      {locationOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {formData.returnLocation?.toLowerCase().includes('air') && (
                      <p className="text-xs text-orange-600 mt-1">✈️ +$50 airport dropoff charge</p>
                    )}
                  </div>
                </div>
                <div className="pt-3 border-t border-gray-200">
                  <span className="text-sm text-gray-600">Duration:</span>
                  <p className="font-medium text-gray-900">
                    {calculateDuration()} day{calculateDuration() !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-600">Pickup:</span>
                    <p className="font-medium text-gray-900">{formatDate(booking.pickUpDate)}</p>
                    <p className="text-sm text-gray-600">
                      {booking.pickUpTime} - {booking.pickUpLocation}
                      {booking.pickUpLocation?.toLowerCase().includes('air') && (
                        <span className="ml-1 text-orange-600">✈️</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600">Return:</span>
                    <p className="font-medium text-gray-900">{formatDate(booking.returnDate)}</p>
                    <p className="text-sm text-gray-600">
                      {booking.returnTime} - {booking.returnLocation}
                      {booking.returnLocation?.toLowerCase().includes('air') && (
                        <span className="ml-1 text-orange-600">✈️</span>
                      )}
                    </p>
                  </div>
                </div>
                {/* Airport charges summary in view mode */}
                {airportCharges > 0 && (
                  <div className="mt-2 p-2 bg-orange-50 rounded text-sm text-orange-700">
                    ✈️ Airport service: {formatCurrency(airportCharges)}
                    {booking.pickUpLocation?.toLowerCase().includes('air') && booking.returnLocation?.toLowerCase().includes('air')
                      ? ' (pickup + dropoff)'
                      : booking.pickUpLocation?.toLowerCase().includes('air')
                        ? ' (pickup)'
                        : ' (dropoff)'}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <span className="text-sm text-gray-600">Duration:</span>
                  <p className="font-medium text-gray-900">
                    {calculateDuration()} day{calculateDuration() !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pricing Details - Editable */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Pricing Details</h3>
          <div className={`rounded-lg p-4 ${isEditMode ? 'bg-blue-50' : 'bg-gray-50'}`}>
            {isEditMode ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        name="totalAmount"
                        value={formData.totalAmount}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Paid Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        name="paidAmount"
                        value={formData.paidAmount}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tax</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        name="taxAmount"
                        value={formData.taxAmount}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Insurance</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        name="insuranceAmount"
                        value={formData.insuranceAmount}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Late Fees</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        name="lateFees"
                        value={formData.lateFees}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Damage Fees</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        name="damageFees"
                        value={formData.damageFees}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cleaning Fees</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        name="cleaningFees"
                        value={formData.cleaningFees}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Additional Charges</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        name="additionalCharges"
                        value={formData.additionalCharges}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Discount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="number"
                        name="discountAmount"
                        value={formData.discountAmount}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
                {/* Airport Charges Display */}
                {airportCharges > 0 && (
                  <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
                    <div className="flex justify-between items-center">
                      <span className="text-orange-800 font-medium">✈️ Airport Charges:</span>
                      <span className="font-bold text-orange-800">{formatCurrency(airportCharges)}</span>
                    </div>
                    <p className="text-xs text-orange-600 mt-1">
                      {formData.pickUpLocation?.toLowerCase().includes('air') && formData.returnLocation?.toLowerCase().includes('air')
                        ? 'Pickup + Dropoff at Perth Airport ($50 each)'
                        : formData.pickUpLocation?.toLowerCase().includes('air')
                          ? 'Pickup at Perth Airport'
                          : 'Dropoff at Perth Airport'}
                    </p>
                  </div>
                )}
                <div className="pt-3 border-t border-gray-300 flex justify-between">
                  <span className="font-semibold text-gray-900">Total:</span>
                  <span className="font-bold text-gray-900 text-lg">{formatCurrency(calculateTotalCharges())}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Balance Due:</span>
                  <span className="font-medium text-red-600">
                    {formatCurrency(calculateTotalCharges() - formData.paidAmount)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Base Amount:</span>
                  <span className="font-medium text-gray-900">{formatCurrency(booking.totalAmount)}</span>
                </div>
                {booking.taxAmount && booking.taxAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax:</span>
                    <span className="font-medium text-gray-900">{formatCurrency(booking.taxAmount)}</span>
                  </div>
                )}
                {booking.insuranceAmount && booking.insuranceAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Insurance:</span>
                    <span className="font-medium text-gray-900">{formatCurrency(booking.insuranceAmount)}</span>
                  </div>
                )}
                {booking.lateFees && booking.lateFees > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Late Fees:</span>
                    <span className="font-medium text-red-600">{formatCurrency(booking.lateFees)}</span>
                  </div>
                )}
                {booking.damageFees && booking.damageFees > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Damage Fees:</span>
                    <span className="font-medium text-red-600">{formatCurrency(booking.damageFees)}</span>
                  </div>
                )}
                {booking.cleaningFees && booking.cleaningFees > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cleaning Fees:</span>
                    <span className="font-medium text-red-600">{formatCurrency(booking.cleaningFees)}</span>
                  </div>
                )}
                {booking.additionalCharges && booking.additionalCharges > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Additional Charges:</span>
                    <span className="font-medium text-gray-900">{formatCurrency(booking.additionalCharges)}</span>
                  </div>
                )}
                {booking.discountAmount && booking.discountAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Discount:</span>
                    <span className="font-medium text-green-600">-{formatCurrency(booking.discountAmount)}</span>
                  </div>
                )}
                {/* Airport Charges Display in View Mode */}
                {airportCharges > 0 && (
                  <div className="flex justify-between bg-orange-50 p-2 rounded -mx-2">
                    <span className="text-orange-800">✈️ Airport Charges:</span>
                    <span className="font-medium text-orange-800">{formatCurrency(airportCharges)}</span>
                  </div>
                )}
                <div className="pt-2 border-t border-gray-300 flex justify-between">
                  <span className="font-semibold text-gray-900">Total:</span>
                  <span className="font-bold text-gray-900 text-lg">{formatCurrency(calculateTotalCharges())}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Paid Amount:</span>
                  <span className="font-medium text-green-600">{formatCurrency(booking.paidAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Balance Due:</span>
                  <span className="font-medium text-red-600">
                    {formatCurrency(calculateTotalCharges() - booking.paidAmount)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Extras - Editable */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Extras</h3>
          <div className={`rounded-lg p-4 ${isEditMode ? 'bg-blue-50' : 'bg-gray-50'}`}>
            {isEditMode ? (
              <div className="space-y-3">
                {/* Current extras */}
                {formData.extras.length > 0 ? (
                  <div className="space-y-2">
                    {formData.extras.map((extra: any, index: number) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <input
                            type="text"
                            value={extra.name}
                            onChange={(e) => handleExtraChange(index, 'name', e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Extra name"
                          />
                          <div className="relative">
                            <span className="absolute left-2 top-1.5 text-gray-500 text-sm">$</span>
                            <input
                              type="number"
                              value={extra.price}
                              onChange={(e) => handleExtraChange(index, 'price', e.target.value)}
                              className="w-full pl-6 pr-2 py-1 border border-gray-300 rounded text-sm"
                              placeholder="Price"
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <input
                            type="number"
                            value={extra.quantity}
                            onChange={(e) => handleExtraChange(index, 'quantity', e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Qty"
                            min="1"
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-700 w-20 text-right">
                          {formatCurrency((extra.price || 0) * (extra.quantity || 1))}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveExtra(index)}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                          title="Remove extra"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm italic">No extras added</p>
                )}
                
                {/* Add extra dropdown */}
                <div className="pt-2 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Add Extra</label>
                  <div className="flex gap-2">
                    <select
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddExtra(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      defaultValue=""
                    >
                      <option value="">Select an extra to add...</option>
                      {availableExtras.map(extra => (
                        <option key={extra.name} value={extra.name}>
                          {extra.name} - ${extra.price} ({extra.description})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Extras total */}
                {formData.extras.length > 0 && (
                  <div className="pt-2 border-t border-gray-200 flex justify-between">
                    <span className="font-medium text-gray-700">Extras Total:</span>
                    <span className="font-bold text-gray-900">{formatCurrency(calculateExtrasTotal())}</span>
                  </div>
                )}
              </div>
            ) : booking.extras && booking.extras.length > 0 ? (
              <div className="space-y-2">
                {booking.extras.map((extra: any, index: number) => {
                  const extraName = extra.name || extra.title || 'Unknown Extra';
                  const extraPrice = extra.price || extra.amount || 0;
                  const extraQuantity = extra.quantity || 1;
                  
                  return (
                    <div key={index} className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-gray-900">{extraName}</p>
                        {extraQuantity > 1 && (
                          <p className="text-sm text-gray-600">Quantity: {extraQuantity}</p>
                        )}
                        {extra.description && (
                          <p className="text-xs text-gray-500">{extra.description}</p>
                        )}
                      </div>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(extraPrice * extraQuantity)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 italic">No extras</p>
            )}
          </div>
        </div>

        {/* Vehicle Condition - Editable */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Vehicle Condition</h3>
          <div className={`rounded-lg p-4 ${isEditMode ? 'bg-blue-50' : 'bg-gray-50'}`}>
            {isEditMode ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Mileage</label>
                  <input
                    type="number"
                    name="startMileage"
                    value={formData.startMileage || ''}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Starting mileage"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Mileage</label>
                  <input
                    type="number"
                    name="endMileage"
                    value={formData.endMileage || ''}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ending mileage"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fuel Level (Start)</label>
                  <select
                    name="fuelLevelStart"
                    value={formData.fuelLevelStart || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select fuel level</option>
                    <option value="Empty">Empty</option>
                    <option value="1/4">1/4 Tank</option>
                    <option value="1/2">1/2 Tank</option>
                    <option value="3/4">3/4 Tank</option>
                    <option value="Full">Full Tank</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fuel Level (End)</label>
                  <select
                    name="fuelLevelEnd"
                    value={formData.fuelLevelEnd || ''}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select fuel level</option>
                    <option value="Empty">Empty</option>
                    <option value="1/4">1/4 Tank</option>
                    <option value="1/2">1/2 Tank</option>
                    <option value="3/4">3/4 Tank</option>
                    <option value="Full">Full Tank</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {booking.startMileage && (
                  <div>
                    <span className="text-sm text-gray-600">Start Mileage:</span>
                    <p className="font-medium text-gray-900">{booking.startMileage} miles</p>
                  </div>
                )}
                {booking.endMileage && (
                  <div>
                    <span className="text-sm text-gray-600">End Mileage:</span>
                    <p className="font-medium text-gray-900">{booking.endMileage} miles</p>
                  </div>
                )}
                {booking.fuelLevelStart && (
                  <div>
                    <span className="text-sm text-gray-600">Fuel Level (Start):</span>
                    <p className="font-medium text-gray-900">{booking.fuelLevelStart}</p>
                  </div>
                )}
                {booking.fuelLevelEnd && (
                  <div>
                    <span className="text-sm text-gray-600">Fuel Level (End):</span>
                    <p className="font-medium text-gray-900">{booking.fuelLevelEnd}</p>
                  </div>
                )}
                {!booking.startMileage && !booking.endMileage && !booking.fuelLevelStart && !booking.fuelLevelEnd && (
                  <p className="text-gray-500 italic col-span-2">No vehicle condition data recorded</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Notes - Editable */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Notes</h3>
          <div className={`space-y-3 ${isEditMode ? '' : ''}`}>
            {isEditMode ? (
              <>
                <div className="bg-blue-50 rounded-lg p-3">
                  <label className="block text-sm font-medium text-blue-900 mb-1">Special Requests:</label>
                  <textarea
                    name="specialRequests"
                    value={formData.specialRequests || ''}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter special requests..."
                  />
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <label className="block text-sm font-medium text-green-900 mb-1">Customer Notes:</label>
                  <textarea
                    name="customerNotes"
                    value={formData.customerNotes || ''}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter customer notes..."
                  />
                </div>
                <div className="bg-yellow-50 rounded-lg p-3">
                  <label className="block text-sm font-medium text-yellow-900 mb-1">Internal Notes:</label>
                  <textarea
                    name="internalNotes"
                    value={formData.internalNotes || ''}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter internal notes..."
                  />
                </div>
              </>
            ) : (
              <>
                {booking.specialRequests && (
                  <div className="bg-blue-50 rounded-lg p-3">
                    <span className="text-sm font-medium text-blue-900">Special Requests:</span>
                    <p className="text-gray-700 mt-1">{booking.specialRequests}</p>
                  </div>
                )}
                {booking.customerNotes && (
                  <div className="bg-green-50 rounded-lg p-3">
                    <span className="text-sm font-medium text-green-900">Customer Notes:</span>
                    <p className="text-gray-700 mt-1">{booking.customerNotes}</p>
                  </div>
                )}
                {booking.internalNotes && (
                  <div className="bg-yellow-50 rounded-lg p-3">
                    <span className="text-sm font-medium text-yellow-900">Internal Notes:</span>
                    <p className="text-gray-700 mt-1">{booking.internalNotes}</p>
                  </div>
                )}
                {!booking.specialRequests && !booking.customerNotes && !booking.internalNotes && (
                  <p className="text-gray-500 italic">No notes available</p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Customer Feedback - Read Only */}
        {(booking.customerRating || booking.customerFeedback) && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Customer Feedback</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              {booking.customerRating && (
                <div className="mb-2">
                  <span className="text-sm text-gray-600">Rating:</span>
                  <div className="flex items-center mt-1">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-5 h-5 ${
                          booking.customerRating && i < booking.customerRating ? 'text-yellow-400' : 'text-gray-300'
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="ml-2 text-gray-700">{booking.customerRating}/5</span>
                  </div>
                </div>
              )}
              {booking.customerFeedback && (
                <div>
                  <span className="text-sm text-gray-600">Feedback:</span>
                  <p className="text-gray-700 mt-1">{booking.customerFeedback}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Alerts */}
        {(booking.hasConflict || booking.requiresApproval || bookingService.isBookingOverdue(booking)) && (
          <div className="space-y-2">
            {booking.hasConflict && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 font-medium">⚠️ This booking has conflicts</p>
              </div>
            )}
            {booking.requiresApproval && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-purple-800 font-medium">👤 This booking requires approval</p>
              </div>
            )}
            {bookingService.isBookingOverdue(booking) && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <p className="text-orange-800 font-medium">⏰ This booking is overdue</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
        <div className="flex justify-between items-center">
          {isEditMode ? (
            <>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Close
              </button>
              <button
                onClick={() => setIsEditMode(true)}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                ✎ Edit Booking
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingDetailsEditableView;
